import React, { useState, useEffect } from 'react';
import { useReview } from '../../context/ReviewContext';
import { useAuth } from '../../context/AuthContext';
import { reviewModel, reviewUtils } from '../../data/reviewModel';

const ProductReviewForm = ({ 
  product, 
  orderId,
  onClose, 
  onSuccess 
}) => {
  const { createReview, loadProductReviews } = useReview();
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    rating: 5,
    title: '',
    comment: '',
    tags: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [existingReview, setExistingReview] = useState(null);

  const availableTags = reviewModel.tags.PRODUCT_TAGS;

  // Check if there's an existing review for this product and order
  useEffect(() => {
    const checkExistingReview = async () => {
      if (product && orderId) {
        try {
          const reviews = await loadProductReviews(product.id);
          const existing = reviews.find(review => 
            review.orderId === orderId && review.reviewerId === currentUser?.uid
          );
          
          if (existing) {
            setExistingReview(existing);
            setIsEditMode(true);
            setFormData({
              rating: existing.rating,
              title: existing.title,
              comment: existing.comment,
              tags: existing.tags || []
            });
          }
        } catch (error) {
          console.error('Error checking existing review:', error);
        }
      }
    };
    
    checkExistingReview();
  }, [product, orderId, loadProductReviews, currentUser?.uid]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRatingChange = (rating) => {
    setFormData(prev => ({
      ...prev,
      rating: parseInt(rating)
    }));
  };

  const handleTagToggle = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag].slice(0, 5) // Limit to 5 tags
    }));
  };

  const getTagLabel = (tag) => {
    const tagLabels = {
      'high_quality': 'คุณภาพดี',
      'fresh': 'สดใหม่',
      'good_value': 'คุ้มค่า',
      'accurate_description': 'ตรงตามคำอธิบาย',
      'fast_delivery': 'ส่งเร็ว',
      'well_packaged': 'บรรจุภัณฑ์ดี',
      'as_advertised': 'ตรงตามโฆษณา',
      'excellent': 'ยอดเยี่ยม',
      'satisfied': 'พอใจ',
      'recommended': 'แนะนำ'
    };
    return tagLabels[tag] || tag;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.comment.trim()) {
      setError('กรุณากรอกหัวข้อและความคิดเห็น');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const reviewData = {
        type: reviewModel.types.PRODUCT,
        productId: product.id,
        orderId: orderId,
        reviewedAt: new Date().toISOString(),
        ...formData
      };

      await createReview(reviewData);
      
      if (onSuccess) {
        onSuccess();
      }
      
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error creating review:', error);
      setError(error.message || 'เกิดข้อผิดพลาดในการสร้างรีวิว');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">
        {isEditMode ? 'แก้ไขรีวิวสินค้า' : 'รีวิวสินค้า'}
      </h2>
      
      {product && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <p className="font-semibold text-gray-800">{product.name}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Rating */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            ให้คะแนน (1-5 ดาว)
          </label>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => handleRatingChange(star)}
                className={`text-3xl transition-colors ${
                  star <= formData.rating
                    ? 'text-yellow-400'
                    : 'text-gray-300'
                } hover:text-yellow-400`}
              >
                ★
              </button>
            ))}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {reviewUtils.getRatingText(formData.rating)}
          </p>
        </div>

        {/* Title */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            หัวข้อรีวิว *
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="สรุปรีวิวของคุณ"
            required
            minLength={5}
            maxLength={100}
          />
        </div>

        {/* Comment */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            รายละเอียดรีวิว *
          </label>
          <textarea
            name="comment"
            value={formData.comment}
            onChange={handleInputChange}
            rows={5}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="บอกเล่าประสบการณ์ของคุณกับสินค้านี้..."
            required
            minLength={5}
            maxLength={1000}
          />
          <p className="text-xs text-gray-500 mt-1">
            {formData.comment.length}/1000 ตัวอักษร
          </p>
        </div>

        {/* Tags */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            แท็ก (เลือกได้สูงสุด 5 ตัว)
          </label>
          <div className="flex flex-wrap gap-2">
            {availableTags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => handleTagToggle(tag)}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  formData.tags.includes(tag)
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {getTagLabel(tag)}
              </button>
            ))}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            disabled={isSubmitting}
          >
            ยกเลิก
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'กำลังบันทึก...' : isEditMode ? 'อัปเดตรีวิว' : 'ส่งรีวิว'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProductReviewForm;




